import { z } from "zod";

import { Locale, SUPPORTED_LOCALES } from "./locale.js";
import { localizedOptionalText } from "./schemas/localizedOptionalText.js";

// AUTHORED TEXT — a free-text field a coach writes, optionally in more
// than one language (PE-21, decided 2026-08-22).
//
// Translations are OPTIONAL, and that is the whole point: a program
// written in one language is valid and complete, and someone writing
// for themselves never has to notice the feature exists. So the stored
// value is EITHER a naked string (what every program written before
// this holds, and what the connector still sends) OR a per-locale
// object. Nothing migrates: the read path understands both shapes.
//
// `autoTranslated` lists the locales whose text was produced by a
// machine rather than typed by the author. It costs nothing now and
// cannot be added later without guessing: without it, a regeneration
// would overwrite the coach's own words, and the athlete could never
// tell whether a nuance came from their coach or from a model. Empty /
// absent means every present locale was written by hand.
const localizedAuthored = (max: number) =>
  localizedOptionalText(max).extend({
    autoTranslated: z.array(Locale).max(SUPPORTED_LOCALES.length).optional(),
  });

// A field that may be absent entirely (description, notes, cues).
//
// ⛔ The naked-string arm carries NO `min(1)`: `z.string().max(n)` is
// what these fields have always been, so programs already stored with
// an empty-string description must keep parsing. Tightening it here
// would reject real production rows on read — the read path treats ""
// as absent instead.
export const authoredText = (max: number) =>
  z.union([
    z.string().max(max),
    // ⛔ The object arm must carry text in at least one language, same as
    // the required fields. `{}` would be an "empty" value that is
    // nonetheless TRUTHY, so every `description && <p>{...}</p>` gate in
    // the app would render an empty element. A field with nothing in it
    // is omitted, not stored as an empty object — and nothing in
    // production holds `{}`, since the object shape is new here.
    localizedAuthored(max).refine(hasSomeText, {
      message: "at least one language must carry text",
    }),
  ]);

// A field the schema declares mandatory (a program has a name). The
// object form must carry text in at least one language — "every locale
// optional" would otherwise admit `{}`, a nameless program that parses
// clean and renders blank.
export const requiredAuthoredText = (max: number) =>
  z.union([
    z.string().min(1).max(max),
    localizedAuthored(max).refine(hasSomeText, {
      message: "at least one language must carry text",
    }),
  ]);

export type AuthoredText = z.infer<ReturnType<typeof authoredText>>;

// Shape the resolver accepts. Deliberately wider than `AuthoredText` so
// `pickLocalized` also serves LocalizedDescription / LocalizedText
// values, which carry the same per-locale keys without the marker.
export type LocalizedLike = Readonly<Partial<Record<Locale, string>>>;

const nonEmpty = (v: string | undefined): v is string =>
  typeof v === "string" && v.trim().length > 0;

function hasSomeText(v: LocalizedLike): boolean {
  return SUPPORTED_LOCALES.some((l) => nonEmpty(v[l]));
}

// Resolve a per-locale object to one string: READER's language first,
// then the AUTHOR's, then any language that has text.
//
// ⛔ The author link is not decoration. Antenore's rule is that the
// default is the language of the coach who wrote it, so a program
// written in French must not fall back to English merely because some
// English exists somewhere in the object. Without `authorLocale` the
// order is unchanged from the original description behaviour, which is
// why the exercise catalog can share this helper.
// ⚠️ `reader` is OPTIONAL on purpose. Callers that resolve text for
// nobody in particular — muscle-adjacency, target projection, any pure
// computation over the cycle — have no reader, and defaulting them to
// "en" would put English back at the head of the chain for exactly the
// programs this feature exists to serve. With no reader the chain
// starts at the author.
export const pickLocalized = (
  value: LocalizedLike | null | undefined,
  reader?: Locale,
  authorLocale?: Locale,
): string | null => {
  if (!value) return null;
  const order: Locale[] = [];
  if (reader) order.push(reader);
  if (authorLocale) order.push(authorLocale);
  for (const l of SUPPORTED_LOCALES) order.push(l);
  for (const l of order) {
    const v = value[l];
    if (nonEmpty(v)) return v;
  }
  return null;
};

// Same resolution, for a field that may still hold the naked string
// every program written before PE-21 carries. This is the function the
// read path calls; `pickLocalized` is the per-locale-object half.
export const pickAuthoredText = (
  value: AuthoredText | string | null | undefined,
  reader?: Locale,
  authorLocale?: Locale,
): string | null => {
  if (value == null) return null;
  if (typeof value === "string") return nonEmpty(value) ? value : null;
  return pickLocalized(value, reader, authorLocale);
};

// Apply a string transform to EVERY language present, preserving the
// value's shape. Renaming a clone ("… (copy)") or capping it to the
// schema ceiling must not collapse a translated field to one language:
// the copy has to arrive with the same languages the original had.
export const mapAuthoredText = <T extends AuthoredText | string>(
  value: T,
  fn: (s: string) => string,
): T => {
  if (typeof value === "string") return fn(value) as T;
  const out: Record<string, unknown> = { ...value };
  for (const l of SUPPORTED_LOCALES) {
    const v = value[l];
    if (nonEmpty(v)) out[l] = fn(v);
  }
  return out as T;
};

// Write `text` into ONE language of an authored value, leaving every
// other language untouched. This is what keeps an edit round-trip
// lossless: a PATCH replaces the whole program object, so an editor that
// rebuilt the field from the string it happened to be showing would
// delete every other translation on save.
//
// ⛔ A value stored as a naked string STAYS a naked string. Programs
// written before PE-21 must not silently become objects the first time
// someone opens and saves them — nothing migrates unless a second
// language is actually added.
export const setAuthoredLocale = (
  previous: AuthoredText | string | null | undefined,
  locale: Locale,
  text: string,
): AuthoredText | undefined => {
  const trimmed = text.trim();
  if (previous == null || typeof previous === "string") {
    return trimmed.length > 0 ? text : undefined;
  }
  const next: Record<string, unknown> = { ...previous };
  if (trimmed.length > 0) next[locale] = text;
  else delete next[locale];
  // Editing a language by hand makes it the author's words again, so it
  // stops being flagged as machine-generated.
  const marks = previous.autoTranslated?.filter((l) => l !== locale);
  if (marks && marks.length > 0) next.autoTranslated = marks;
  else delete next.autoTranslated;
  return hasSomeText(next as LocalizedLike)
    ? (next as AuthoredText)
    : undefined;
};

// Which language a value actually carries text in, preferring the
// author's. The editor uses it to decide which language it is WRITING:
// opening a translated program must put the coach back on the words
// they wrote, not on a translation of them.
export const authoredLocaleOf = (
  value: AuthoredText | string | null | undefined,
  authorLocale?: Locale,
): Locale | null => {
  if (value == null || typeof value === "string") return authorLocale ?? null;
  if (authorLocale && nonEmpty(value[authorLocale])) return authorLocale;
  return SUPPORTED_LOCALES.find((l) => nonEmpty(value[l])) ?? authorLocale ?? null;
};

// Read ONE language, with no fallback chain at all (PE-21 slice B).
//
// ⛔ This is what the EDITOR reads, and it is deliberately not
// `pickAuthoredText`. The reader → author → any-available chain exists so
// nobody sees a blank screen; an AUTHOR who switches to French and is
// shown the Italian would type over it believing they were translating,
// and end up with the Italian destroyed and the French never written.
// Empty means empty: that is the honest thing to show someone whose job
// is to fill it in.
//
// A naked string belongs to the language it was written in, which is
// `authorLocale` when we know it — see textForEditing below.
export const authoredTextIn = (
  value: AuthoredText | string | null | undefined,
  locale: Locale,
  authorLocale?: Locale,
): string => {
  if (value == null) return "";
  if (typeof value === "string")
    return authorLocale === undefined || authorLocale === locale ? value : "";
  return value[locale] ?? "";
};

// Turn a naked string into a per-language object, filing the text under
// the language it was written in.
//
// ⛔ Called ONLY when a second language is actually requested. Slice A's
// promise is that nothing migrates: a program nobody translates keeps its
// naked strings for ever, including across saves. The moment a coach asks
// for a second language that promise has served its purpose — there is
// nowhere else to put the first one.
export const promoteToLocalized = (
  value: AuthoredText | string | null | undefined,
  authorLocale: Locale,
): AuthoredText | undefined => {
  if (value == null) return undefined;
  if (typeof value !== "string") return value;
  return nonEmpty(value) ? { [authorLocale]: value } : undefined;
};

// True when this language's text came from a machine rather than from the
// author. The UI marks it so a regeneration never silently overwrites
// hand-written words, and so an athlete can tell whose nuance they are
// reading.
export const isAutoTranslated = (
  value: AuthoredText | string | null | undefined,
  locale: Locale,
): boolean =>
  value != null &&
  typeof value !== "string" &&
  (value.autoTranslated?.includes(locale) ?? false);
